import { isTaskCloseableByMember, isTaskRepeating } from '@frontend/api-client';
import type { DraftTask, TaskDetail } from '@frontend/api-types';
import { useCallback, useFeatureIsOn, useMemo, useTempFeatureFlag } from '@frontend/react';
import type { DropdownMenuProps } from '@frontend/web-react';
import { Button, DropdownMenu, Icon, Text, XStack } from '@frontend/web-react';
import { useAccessControl, useRetrieveClientQuery, useRetrieveHouseholdQuery } from '@frontend/web/hooks';
import { useNavigate, useRouter } from '@tanstack/react-router';

type TaskDetailHeaderProps = {
  draftTask?: Pick<DraftTask, 'id'>;
  onCloseTask: () => void;
  onOpenSummary: () => void;
  onShareTask: () => void;
  onShareWithHousehold?: () => void;
  onToggleSearch?: () => void;
  task?: TaskDetail;
};

export const TaskDetailHeader = ({
  draftTask,
  onCloseTask,
  onOpenSummary,
  onShareTask,
  onShareWithHousehold,
  onToggleSearch,
  task,
}: TaskDetailHeaderProps) => {
  const { isClientSubscribed } = useAccessControl();
  const { enabled: isRecurrencesRevampedEnabled } = useFeatureIsOn('is_recurrences_revamped_enabled');
  const { enabled: isShareTaskEnabled } = useFeatureIsOn('is_share_task_enabled');
  const isInTaskSearchEnabled = useTempFeatureFlag('is_in_task_search_enabled');
  const router = useRouter();
  const navigate = useNavigate();
  const { data: client } = useRetrieveClientQuery();
  const { data: household } = useRetrieveHouseholdQuery(client?.household ?? '', {
    skip: !client?.household,
  });

  const handleFeedback = useCallback(() => {
    navigate(
      task || draftTask
        ? {
            search: { ...(task?.id ? { taskId: task.id } : {}), ...(draftTask?.id ? { draftId: draftTask.id } : {}) },
            to: '/app/settings/feedback',
          }
        : { to: '/app/settings/feedback' },
    );
  }, [navigate, task, draftTask]);

  const mobileOverflowOptions: DropdownMenuProps['layout'] = useMemo(
    () => [
      ...(task
        ? [{ icon: 'IconFlag' as const, label: 'Feedback', onClick: handleFeedback, type: 'item' as const }]
        : []),
      ...(task && isShareTaskEnabled
        ? [{ icon: 'IconShare2' as const, label: 'Share', onClick: onShareTask, type: 'item' as const }]
        : []),
      ...(task && onToggleSearch && isInTaskSearchEnabled
        ? [{ icon: 'IconSearch' as const, label: 'Search', onClick: onToggleSearch, type: 'item' as const }]
        : []),
      ...(household?.members?.length
        ? [
            {
              icon: 'IconUserPlus' as const,
              label: 'Share with household',
              onClick: () => onShareWithHousehold?.(),
              type: 'item' as const,
            },
          ]
        : []),
      { icon: 'IconFileDescription' as const, label: 'Summary', onClick: onOpenSummary, type: 'item' as const },
      ...(task && isTaskCloseableByMember(task) && isClientSubscribed
        ? [{ icon: 'IconCircleCheck' as const, label: 'Close task', onClick: onCloseTask, type: 'item' as const }]
        : []),
    ],
    [
      household,
      task,
      isShareTaskEnabled,
      isInTaskSearchEnabled,
      isClientSubscribed,
      handleFeedback,
      onShareTask,
      onToggleSearch,
      onShareWithHousehold,
      onOpenSummary,
      onCloseTask,
    ],
  );

  return (
    <XStack
      alignItems="center"
      borderBottom
      borderColor="neutral200"
      className="md:relative md:top-auto sticky top-none z-10 bg-trueWhite"
      justifyContent="between"
      px="md"
      py="md"
    >
      <XStack alignItems="center" gap="sm">
        <Icon name="IconChevronLeft" onClick={router.history.back} size="lg" />
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <Text className="font-[500] text-neutral-950" lineClamp={1} type="p-md">
          {task?.name}
        </Text>
        {isRecurrencesRevampedEnabled && task && isTaskRepeating(task) ? (
          <XStack alignItems="center" gap="xs">
            <Icon className="text-textSecondary" name="IconRefresh" size={16} />
            <Text color="secondary" type="p-sm">
              Recurring
            </Text>
          </XStack>
        ) : null}
      </XStack>
      <XStack alignItems="center" className="hidden md:flex" gap="sm">
        {task ? (
          <Button leftIconName="IconFlag" onClick={handleFeedback} size="sm" variant="secondary">
            Feedback
          </Button>
        ) : null}
        {task && isShareTaskEnabled ? (
          <Button leftIconName="IconShare2" onClick={onShareTask} size="sm" variant="secondary">
            Share
          </Button>
        ) : null}
        {task && onToggleSearch && isInTaskSearchEnabled ? (
          <Icon name="IconSearch" onClick={onToggleSearch} size="lg" />
        ) : null}
        {household?.members?.length ? <Icon name="IconUserPlus" onClick={onShareWithHousehold} size="lg" /> : null}
        <Icon name="IconFileDescription" onClick={onOpenSummary} size="lg" />
        {task && isTaskCloseableByMember(task) && isClientSubscribed ? (
          <Icon accessibilityLabel="Close task" name="IconCircleCheck" onClick={onCloseTask} size="lg" />
        ) : null}
      </XStack>
      <XStack alignItems="center" className="flex md:hidden">
        <DropdownMenu layout={mobileOverflowOptions} trigger="click" width={200}>
          <Icon name="IconDots" size="lg" />
        </DropdownMenu>
      </XStack>
    </XStack>
  );
};
